/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/react';
import { Image as ImageIcon, User } from 'phosphor-react';
import { FieldValues, UseFormRegister } from 'react-hook-form';
import { UploadDropzone, UploadPreviewRow } from '../../../ui/UploadDropzone';

interface ImageInputFormProps {
  defaultVal: any;
  setImages: (image: any) => void;
  setIsUpping: (state: boolean) => void;
  name: string | undefined | any;
  label: string | undefined;
  useAvatar?: boolean;
  isRequired?: boolean;
  register: UseFormRegister<FieldValues>;
  watch: any;
  errors: any;
  setValue?: (name: string, value: any) => void;
  compact?: boolean;
  variant?: 'avatar' | 'inline' | 'block';
  avatarName?: string;
}

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileNameFromUrl = (url: string) => {
  try {
    const path = new URL(url).pathname;
    return decodeURIComponent(path.split('/').pop() || 'imagem');
  } catch {
    return 'imagem';
  }
};

export const ImageInputForm = ({
  defaultVal,
  setImages,
  setIsUpping,
  name,
  label,
  isRequired,
  register,
  watch,
  errors,
  setValue,
  compact,
  variant,
  avatarName,
}: ImageInputFormProps) => {
  let resolvedVariant: 'avatar' | 'inline' | 'block' = variant ?? 'inline';
  if (!variant && compact === false) resolvedVariant = 'block';
  const isCompact = resolvedVariant === 'inline';
  const [preview, setPreview] = useState<string | undefined>(undefined);
  const [removed, setRemoved] = useState(false);
  const selected = watch(name);
  const file: File | undefined =
    selected && typeof selected === 'object' && selected.length > 0 ? selected[0] : undefined;

  useEffect(() => {
    if (!file) {
      setPreview(undefined);
      return undefined;
    }
    setIsUpping(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImages(reader.result);
      setIsUpping(false);
    };
    reader.readAsDataURL(file);
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    setRemoved(false);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const existingUrl: string | undefined = !removed && !file ? defaultVal?.original : undefined;
  const hasError = !!errors[name];

  const clearSelection = () => {
    if (setValue) setValue(name, null);
    setImages('');
    setPreview(undefined);
    setRemoved(true);
  };

  const dropFiles = (files: FileList) => {
    const input = document.getElementById(name) as HTMLInputElement | null;
    if (!input) return;
    input.files = files;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const currentSrc = preview ?? existingUrl;

  let content = (
    <UploadDropzone
      inputId={name}
      title="Clique para enviar"
      onFiles={dropFiles}
      description="PNG, JPG ou WEBP"
      isInvalid={hasError}
      icon={<ImageIcon size={isCompact ? 16 : 20} />}
      compact={isCompact}
    />
  );
  if (resolvedVariant === 'avatar') {
    content = (
      <Flex align="center" gap="16px">
        <Avatar
          size="lg"
          w="64px"
          h="64px"
          flexShrink={0}
          src={currentSrc}
          name={currentSrc ? undefined : avatarName}
          bg="surface.selected"
          color="content.subtle"
          icon={<User size={24} />}
          border="1px solid"
          borderColor="line.hairline"
        />
        <Box flex="1" minW={0} display="flex" flexDirection="column" gap="8px">
          <UploadDropzone
            inputId={name}
            title={currentSrc ? 'Trocar imagem' : 'Clique para enviar'}
            onFiles={dropFiles}
            description="PNG, JPG ou WEBP"
            isInvalid={hasError}
            icon={<ImageIcon size={16} />}
            compact
          />
          {currentSrc && setValue && (
            <Flex>
              <Button type="button" size="xs" variant="ghost" onClick={clearSelection}>
                Remover imagem
              </Button>
            </Flex>
          )}
        </Box>
      </Flex>
    );
  } else if (file && preview) {
    content = (
      <UploadPreviewRow
        name={file.name}
        meta={`${formatSize(file.size)} · ${file.type.replace('image/', '').toUpperCase()}`}
        previewSrc={preview}
        replaceInputId={name}
        onRemove={setValue ? clearSelection : undefined}
      />
    );
  } else if (existingUrl) {
    content = (
      <UploadPreviewRow
        name={fileNameFromUrl(existingUrl)}
        meta="Imagem atual"
        previewSrc={existingUrl}
        replaceInputId={name}
        onOpen={() => window.open(existingUrl, '_blank', 'noopener,noreferrer')}
      />
    );
  }

  return (
    <FormControl isRequired={isRequired} isInvalid={hasError}>
      <FormLabel htmlFor={name}>{label}</FormLabel>
      {content}
      <Box as="input" id={name} type="file" hidden accept="image/*" {...register(name)} />
      {hasError && <FormErrorMessage>{errors[name]?.message}</FormErrorMessage>}
    </FormControl>
  );
};
